// HousekeepingTasks.js  Task list + assign + status changes.
import React, { useEffect, useState } from 'react';
import {
  Box, Container, Card, CardContent, Typography, TextField, Button, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Snackbar, Alert, CircularProgress, MenuItem, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DoneIcon from '@mui/icons-material/Done';
import VerifiedIcon from '@mui/icons-material/Verified';
import CancelIcon from '@mui/icons-material/Cancel';
import { housekeepingApi, ctx } from './modulesApi';

const TASK_TYPES = ['FullCleaning', 'PartialCleaning', 'LinenChange', 'DeepCleaning', 'Maintenance'];
const PRIORITIES = ['Low', 'Normal', 'High', 'Urgent'];
const NEXT = { Assigned: 'InProgress', InProgress: 'Completed', Completed: 'Verified' };

export default function HousekeepingTasks() {
  const { companyId, hotelId, userId } = ctx();
  const [items, setItems] = useState([]); const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState(''); const [loading, setLoading] = useState(false);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    roomId: 0, roomNumber: '', staffId: null, taskType: 'FullCleaning',
    priority: 'Normal', remarks: '',
  });
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (m, s = 'success') => setSnack({ open: true, msg: m, sev: s });

  const load = async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const r = await housekeepingApi.listTasks(hotelId, {
        status: statusFilter || undefined, page: page + 1, pageSize: rowsPerPage,
      });
      setItems(r.data.items || []); setTotal(r.data.totalCount || 0);
    } finally { setLoading(false); }
  };
  const loadStaff = async () => {
    const r = await housekeepingApi.listStaff(hotelId, { pageSize: 200 });
    setStaff(r.data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, rowsPerPage, statusFilter]);
  useEffect(() => { loadStaff(); /* eslint-disable-next-line */ }, []);

  const onCreate = async () => {
    if (!form.roomId) return notify('Room ID required', 'warning');
    try {
      await housekeepingApi.createTask({ companyId, hotelId, ...form, createdBy: userId });
      notify('Task created'); setOpen(false); load();
    } catch { notify('Failed', 'error'); }
  };

  const advance = async (task) => {
    const to = NEXT[task.status]; if (!to) return;
    try {
      await housekeepingApi.changeTaskStatus({ taskId: task.taskId, toStatus: to, changedBy: userId });
      notify(`${task.status}  ${to}`); load();
    } catch (e) { notify(e.response?.data?.error || 'Failed', 'error'); }
  };
  const cancelTask = async (task) => {
    if (!window.confirm('Cancel task?')) return;
    try {
      await housekeepingApi.changeTaskStatus({ taskId: task.taskId, toStatus: 'Cancelled', changedBy: userId });
      notify('Cancelled'); load();
    } catch { notify('Failed', 'error'); }
  };

  const statusChip = (s) => (
    <Chip size='small' label={s} color={
      s === 'Completed' || s === 'Verified' ? 'success'
        : s === 'InProgress' ? 'warning'
        : s === 'Cancelled' ? 'error' : 'info'
    } />
  );

  return (
    <Container maxWidth='xl' sx={{ py: 3 }}>
      <Card>
        <CardContent>
          <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
            <Typography variant='h5'>Housekeeping Tasks</Typography>
            <Button variant='contained' startIcon={<AddIcon />} onClick={() => {
              setForm({ roomId: 0, roomNumber: '', staffId: null, taskType: 'FullCleaning',
                        priority: 'Normal', remarks: '' });
              setOpen(true);
            }}>Assign Task</Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <TextField select fullWidth size='small' label='Status' value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value=''>All</MenuItem>
                {['Assigned', 'InProgress', 'Completed', 'Verified', 'Cancelled'].map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <TableContainer><Table size='small'>
            <TableHead><TableRow>
              <TableCell>Room</TableCell><TableCell>Task</TableCell>
              <TableCell>Priority</TableCell><TableCell>Staff</TableCell>
              <TableCell>Assigned</TableCell><TableCell>Status</TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {loading && (<TableRow><TableCell colSpan={7} align='center'><CircularProgress size={24} /></TableCell></TableRow>)}
              {!loading && items.length === 0 && (<TableRow><TableCell colSpan={7} align='center'>No tasks</TableCell></TableRow>)}
              {items.map((t) => (
                <TableRow key={t.taskId} hover>
                  <TableCell>{t.roomNumber || t.roomId}</TableCell>
                  <TableCell>{t.taskType}</TableCell>
                  <TableCell><Chip size='small' label={t.priority}
                    color={t.priority === 'Urgent' ? 'error' : t.priority === 'High' ? 'warning' : 'default'} /></TableCell>
                  <TableCell>{t.staffName || ''}</TableCell>
                  <TableCell>{t.assignedAt ? new Date(t.assignedAt).toLocaleString('en-GB') : ''}</TableCell>
                  <TableCell>{statusChip(t.status)}</TableCell>
                  <TableCell align='right'>
                    {NEXT[t.status] && (
                      <Tooltip title={`Move to ${NEXT[t.status]}`}>
                        <IconButton size='small' color='primary' onClick={() => advance(t)}>
                          {t.status === 'Assigned' ? <PlayArrowIcon /> :
                           t.status === 'InProgress' ? <DoneIcon /> : <VerifiedIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {!['Completed', 'Verified', 'Cancelled'].includes(t.status) && (
                      <Tooltip title='Cancel'><IconButton size='small' color='error' onClick={() => cancelTask(t)}>
                        <CancelIcon /></IconButton></Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></TableContainer>
          <TablePagination component='div' count={total} page={page}
            onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]} />
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Assign Task</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth type='number' label='Room ID' value={form.roomId}
              onChange={(e) => setForm({ ...form, roomId: parseInt(e.target.value, 10) || 0 })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label='Room Number' value={form.roomNumber}
              onChange={(e) => setForm({ ...form, roomNumber: e.target.value })} /></Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label='Task Type' value={form.taskType}
                onChange={(e) => setForm({ ...form, taskType: e.target.value })}>
                {TASK_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label='Priority' value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label='Assign To' value={form.staffId || ''}
                onChange={(e) => setForm({ ...form, staffId: e.target.value ? parseInt(e.target.value, 10) : null })}>
                <MenuItem value=''>--Unassigned--</MenuItem>
                {staff.filter((s) => s.isActive).map((s) => (
                  <MenuItem key={s.staffId} value={s.staffId}>{s.staffName} ({s.shift})</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth label='Remarks' value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={onCreate}>Create</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
